import React, { useState } from "react";
import "./ApplicationForm.css";
import { saveApplication } from "../firebase";

// TODO
// only display " Remplis tes infos " when a job is selected
// any click on the disabled form should highlight in yellow "Choisis ton poste" and the field-note
// remove hover effect on CTA button when form is disabled
// scale down the illustration image

const ApplicationForm = ({ job = "" }) => {
  // controlled form fields
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [social, setSocial] = useState("");
  const [intro, setIntro] = useState("");

  // CV upload state & handlers (commented out for now)
  const [cvFile, setCvFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // both handleFileChange and handleDrop share similar logic
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (
      file &&
      ["application/pdf", "image/png", "image/jpeg"].includes(file.type)
    ) {
      setCvFile(file);
    } else {
      setErrors((prev) => ({
        ...prev,
        cv: "Please upload a PDF, PNG, or JPEG file.",
      }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (
      file &&
      ["application/pdf", "image/png", "image/jpeg"].includes(file.type)
    ) {
      setCvFile(file);
      setErrors((prev) => ({ ...prev, cv: undefined }));
    } else {
      setErrors((prev) => ({
        ...prev,
        cv: "Please upload a PDF, PNG, or JPEG file.",
      }));
    }
  };

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [serverError, setServerError] = useState("");

  const validate = () => {
    const errs = {};
    if (!firstName.trim()) errs.firstName = "First name is required";
    if (!lastName.trim()) errs.lastName = "Last name is required";
    if (!email.trim()) errs.email = "Email is required";
    else {
      // simple email regex
      const re =
        // eslint-disable-next-line
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@\"]+\.)+[^<>()[\]\\.,;:\s@\"]{2,})$/i;
      if (!re.test(email)) errs.email = "Email is invalid";
    }
    return errs;
  };

  const resetMessages = () => {
    setSuccessMessage("");
    setServerError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    try {
      // build payload
      const payload = {
        email,
        firstName,
        lastName,
        link: social,
        intro,
        job: job || "",
        cv: { ...cvFile, downloadURL: null }, // downloadURL to be set after upload
      };

      await saveApplication(payload);

      setSuccessMessage("Application sent — we will be in touch.");
      // optionally clear the form
      setEmail("");
      setFirstName("");
      setLastName("");
      setSocial("");
      setIntro("");
      setErrors({});
    } catch (err) {
      setServerError(
        err.message || "Failed to submit. Please try again later."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="application-form" onSubmit={handleSubmit} noValidate>
      {/* Selected job (hidden) */}
      <input type="hidden" name="selectedJob" value={job || ""} />

      {/* success / server error messages */}
      {successMessage && <div className="form-success">{successMessage}</div>}
      {serverError && <div className="form-error">{serverError}</div>}

      {/* disable the form until a job is selected */}
      {!job && (
        <div className="field-note" role="status">
          Veuillez sélectionner un poste pour activer le formulaire.
        </div>
      )}
      <fieldset disabled={!job} aria-disabled={!job}>
        {/* Email */}
        <div className="form-group">
          <label htmlFor="email">
            email <span className="required">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={!!errors.email}
          />
          {errors.email && <div className="field-error">{errors.email}</div>}
        </div>

        {/* Name fields */}
        <div className="form-row">
          <div className="form-group half">
            <label htmlFor="firstName">
              prénom <span className="required">*</span>
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              placeholder="prénom"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              aria-invalid={!!errors.firstName}
            />
            {errors.firstName && (
              <div className="field-error">{errors.firstName}</div>
            )}
          </div>
          <div className="form-group half">
            <label htmlFor="lastName">
              nom <span className="required">*</span>
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              placeholder="nom"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              aria-invalid={!!errors.lastName}
            />
            {errors.lastName && (
              <div className="field-error">{errors.lastName}</div>
            )}
          </div>
        </div>

        {
          // CV upload (commented out)
          <div
            className={`file-drop-zone ${dragActive ? "drag-active" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
          >
            {cvFile ? (
              <span>Uploaded: {cvFile.name}</span>
            ) : (
              <span>
                Drag your CV{" "}
                <span className="small">(.pdf, .png, .jpg only) or</span>
              </span>
            )}

            <label className="browse-link">
              browse
              <input
                type="file"
                accept=".pdf,.png, .jpg, .jpeg"
                onChange={handleFileChange}
                hidden
              />
            </label>
            {errors.cv && <div className="field-error">{errors.cv}</div>}
          </div>
        }

        {/* Social link */}
        <div className="form-group">
          <label htmlFor="social">portfolio ou social link</label>
          <input
            id="social"
            name="social"
            type="url"
            placeholder="https://..."
            value={social}
            onChange={(e) => setSocial(e.target.value)}
          />
        </div>

        {/* Text area */}
        <div className="form-group">
          <label htmlFor="intro">mot d'introduction</label>
          <textarea
            id="intro"
            name="intro"
            placeholder="brève intro"
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="application-form__cta"
          disabled={submitting || !job}
        >
          {submitting ? "Envoi..." : "Postuler"}
        </button>
      </fieldset>
    </form>
  );
};

export default ApplicationForm;
